import React, { useEffect, useMemo, useCallback } from 'react';

import { useLazyQuery, useMutation } from '@apollo/client';
import {
  gqlGetCpSubgroup,
  gqlAddCpSubgroup,
  gqlUpdateCpSubgroup,
  gqlDeleteCpSubgroup,
} from './graphql';
import { useParams, useHistory, useLocation, generatePath } from 'react-router-dom';

import * as Yup from 'yup';
import * as Lo from 'lodash';
import { getRouteByName } from 'app/routes';
import {
  FieldInput,
  FormCard,
  FormCol,
  FormRow,
  isNumeric,
  promiseModalDialog,
  toastSuccess,
  UserTooltip,
  useWhyDidYouUpdate,
  YupNumeric,
  YupStringReq,
} from 'motion-components';
import { useHandleError } from 'shared-pages/services';
import { FieldSelectGroup } from 'shared-pages/components';

const formShape = {
  name: YupStringReq,
  groupId: YupNumeric,
  /* cpGroup: null, */
};

const validationSchema = Yup.object().shape(formShape);
const pickProperties = (values) => Lo.pick(values, Lo.keys(formShape));
type FormValuesType = Yup.InferType<typeof validationSchema>;

export const CpSubGroupPage = () => {
  /* const { setFieldValue } = useFormikContext<any>(); */
  const { handleGraphqlError } = useHandleError('cpsubgroup');
  const history = useHistory();
  const location = useLocation();
  const { id, selected, name } = useParams<{ id: string; selected: string; name: string }>();

  const idNum = isNumeric(id) ? parseInt(id) : -1;
  const isNew = idNum < 0;

  const newRecord = useMemo<FormValuesType>(
    () =>
      ({
        id: -1,
        name: null,
        groupId: parseInt(selected),
        /* cpGroup: null, */
      } as FormValuesType),
    [selected],
  );

  const [addRecord] = useMutation(gqlAddCpSubgroup);
  const [updateRecord] = useMutation(gqlUpdateCpSubgroup);
  const [deleteRecord] = useMutation(gqlDeleteCpSubgroup);

  const [get, { data: rawData, error, refetch }] = useLazyQuery(gqlGetCpSubgroup);
  /* const [getGroup, { data: rawGroupData }] = useLazyQuery(gqlGetCpGroup); */
  const data = isNew ? newRecord : rawData ? rawData.getCpSubgroupById : undefined;
  /* const groupName = rawGroupData ? rawGroupData.getCpGroupById.name : undefined; */

  useWhyDidYouUpdate('CpSubGroupPage', { data, selected, name });

  /* fetch data */
  useEffect(() => {
    if (!isNew) {
      get({ variables: { id: idNum } });
    }
    /* getGroup({ variables: { id: parseInt(selected) } });
    setFieldValue('cpGroup.name', groupName); */
  }, [get, id, idNum, isNew, selected]);

  /**
   * * ΑΠΟΘΗΚΕΥΣΗ - UPDATE/CREATE
   */
  const handleSubmit = (values: FormValuesType, actions) => {
    actions.setSubmitting(false);
    /* delete values.cpGroup; */
    // Αν είναι νέα εγγραφή τοτε γράφω όλα τα πεδία
    if (isNew) {
      console.log('[CpSubgroup] New:', values);
      addRecord({ variables: { data: { ...values } } })
        .then((d) => {
          toastSuccess(`Επιτυχής Προσθήκη Εγγραφής [${d.data.addCpSubgroup.id}]`);
          handleReturn();
        })
        .catch((error) => {
          handleGraphqlError('mutation-add', error);
        });
    } else {
      // Αν είναι update εγγραφής τοτε γράφω μονο τα αλλαγμένα πεδία
      console.log('[CpSubgroup] Update:', values);
      updateRecord({ variables: { data: { ...values, id: idNum } } })
        .then((d) => {
          toastSuccess(`Επιτυχής Ενημέρωση Εγγραφής [${d.data.updateCpSubgroup.id}]`);
          refetch();
        })
        .catch((error) => {
          handleGraphqlError('mutation-update', error);
        });
    }
  };

  /**
   * *  DELETE
   */
  const handleDelete = useCallback(async () => {
    const res = await promiseModalDialog({
      title: 'Διαγραφή Εγγραφής',
      text: 'Είστε σίγουροι ότι θέλετε να διαγράψετε την Εγγραφή;',
    });
    if (res) {
      deleteRecord({ variables: { id: idNum } })
        .then(() => history.push(generatePath(getRouteByName('CpGroups'))))
        .catch((error) => {
          handleGraphqlError('mutation-delete', error);
        });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deleteRecord, idNum]);

  // Handler να πάει στην πίσω σελίδα
  const handleReturn = useCallback(() => {
    history.push(generatePath(getRouteByName('CpGroups')), { ...(location as any).state });
  }, [history, location.state]);

  if (error) {
    handleGraphqlError('query-get', error);
    return <h3 className="text-center">{error.message}</h3>;
  } else {
    return (
      <FormCard
        isNew={isNew}
        title={isNew ? 'Υποκατηγορία: Εισαγωγή Νέας' : `Υποκατηγορία: ${data?.name}`}
        onSubmit={handleSubmit}
        onReturn={null}
        onDelete={handleDelete}
        permitDelete={true}
        initialValues={pickProperties(data)}
        validationSchema={validationSchema}
        enableReinitialize
        idx={idNum}
      >
        <FormRow>
          {/* <GroupName name={groupName} /> */}
          <FieldSelectGroup as={FormCol} disabled={true} defaultValues={data}></FieldSelectGroup>
        </FormRow>
        <FormRow>
          <SubGroupName />
        </FormRow>
        <div className="float-end">{data && !isNew && <UserTooltip data={data} />}</div>
      </FormCard>
    );
  }
};

// ---------------------- DATA FIELDS -----------------------
const SubGroupName = () => {
  return (
    <FieldInput as={FormCol} label="Υποκατηγορία" name="name" placeholder="- υποκατηγορία -" />
  );
};
/* const GroupName = name => {
  return (
    <FieldInput
      disabled={true}
      as={FormCol}
      defaultValues={{ name }}
      label="Κατηγορία"
      name="cpGroup.name"
      placeholder="- κατηγορία -"
    />
  );
}; */

